import type { AdminUser, Role } from "@divespots/contracts";
import { AdminUserCreate } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp, formatTimestampShort } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { useSession } from "./session.ts";
import { ConfirmSheet, Field, Section } from "./ui.tsx";

// a self-registered diver can be promoted here; staff can also be demoted back to diver
const ROLES: Role[] = ["diver", "moderator", "admin"];
const DAY_MS = 24 * 3600_000;

export interface UserFilters {
  q: string;
  role: Role | "";
  status: "" | "active" | "blocked";
  /** Client-side: registered in the last 24 hours (to spot a wave of bot accounts). */
  recent: boolean;
}
export const NO_FILTERS: UserFilters = { q: "", role: "", status: "", recent: false };

/** The server query for the filters (`recent` is applied on the result, see `applyRecent`). */
export function usersQuery(f: UserFilters) {
  return {
    ...(f.q.trim() ? { q: f.q.trim() } : {}),
    ...(f.role ? { role: f.role } : {}),
    ...(f.status
      ? { banned: (f.status === "blocked" ? "true" : "false") as "true" | "false" }
      : {}),
  };
}

/** "Neu in den letzten 24 h": newest accounts of the last day. */
export function applyRecent(users: readonly AdminUser[], recent: boolean, now = Date.now()) {
  return recent ? users.filter((u) => now - new Date(u.createdAt).getTime() < DAY_MS) : users;
}

/** Admin only: find, filter, create (initial password), change role, block/unblock. */
export function UsersPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const qc = useQueryClient();
  const { user: me } = useSession();
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>(NO_FILTERS);
  const [confirm, setConfirm] = useState<AdminUser | null>(null);

  // the search text is sent after a short pause; the other filters at once
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(filters.q), 250);
    return () => clearTimeout(id);
  }, [filters.q]);
  const query = usersQuery({ ...filters, q: qDebounced });

  const q = useQuery({
    queryKey: ["admin", "users", query],
    queryFn: ({ signal }) => api.listUsers(query, signal).then((r) => r.users),
    placeholderData: (prev) => prev,
  });

  const patch = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { role?: Role; banned?: boolean; diveAccess?: boolean };
    }) => api.updateUser(id, body),
    onSuccess: async () => {
      setNotice(null);
      setConfirm(null);
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e) => {
      setConfirm(null);
      setNotice(
        isApiRequestError(e) && e.code === "conflict"
          ? t("admin.users.selfConflict")
          : t("admin.err.save"),
      );
    },
  });

  const users = useMemo(() => applyRecent(q.data ?? [], filters.recent), [q.data, filters.recent]);
  const set = (p: Partial<UserFilters>) => setFilters((f) => ({ ...f, ...p }));

  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("admin.users.title")}</h1>

      <form
        className="adm-filters"
        role="search"
        aria-label={t("admin.users.title")}
        onSubmit={(e) => e.preventDefault()}
      >
        <label className="adm-filters__q">
          <span className="ds-text-footnote text-label-secondary">{t("admin.users.search")}</span>
          <input
            className="adm-input"
            type="search"
            autoComplete="off"
            value={filters.q}
            onChange={(e) => set({ q: e.target.value })}
          />
        </label>
        <label>
          <span className="ds-text-footnote text-label-secondary">
            {t("admin.users.filterRole")}
          </span>
          <select
            className="adm-input"
            value={filters.role}
            onChange={(e) => set({ role: e.target.value as Role | "" })}
          >
            <option value="">{t("admin.users.all")}</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.role.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ds-text-footnote text-label-secondary">
            {t("admin.users.filterStatus")}
          </span>
          <select
            className="adm-input"
            value={filters.status}
            onChange={(e) => set({ status: e.target.value as UserFilters["status"] })}
          >
            <option value="">{t("admin.users.all")}</option>
            <option value="active">{t("admin.users.active")}</option>
            <option value="blocked">{t("admin.users.blocked")}</option>
          </select>
        </label>
        <label className="adm-check adm-filters__recent">
          <input
            type="checkbox"
            checked={filters.recent}
            onChange={(e) => set({ recent: e.target.checked })}
          />
          <span>{t("admin.users.new24h")}</span>
        </label>
      </form>

      {q.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {notice ? (
        <p className="adm-error" role="alert">
          {notice}
        </p>
      ) : null}

      {q.data ? (
        <p className="ds-text-footnote text-label-secondary m-0" role="status">
          {users.length === 0
            ? t("admin.users.empty")
            : t("admin.users.count", { count: users.length })}
        </p>
      ) : null}

      {users.length > 0 ? (
        <table className="adm-table" aria-label={t("admin.users.title")}>
          <thead>
            <tr>
              <th scope="col" className="adm-col-user">
                {t("admin.users.user")}
              </th>
              <th scope="col" className="adm-col-role">
                {t("admin.users.role")}
              </th>
              <th scope="col" className="adm-col-dive">
                {t("admin.users.diveMode")}
              </th>
              <th scope="col" className="adm-col-date">
                {t("admin.users.registered")}
              </th>
              <th scope="col" className="adm-col-date">
                {t("admin.users.lastSeen")}
              </th>
              <th scope="col" className="adm-col-num" title={t("admin.users.pending")}>
                <span aria-hidden="true">{t("admin.users.pendingShort")}</span>
                <span className="sr-only">{t("admin.users.pending")}</span>
              </th>
              <th scope="col" className="adm-col-actions">
                <span className="sr-only">{t("admin.users.ban")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isSelf={u.id === me?.id}
                busy={patch.isPending}
                locale={locale}
                // optimistic while the PATCH runs; on an error the stored value shows again
                diveAccess={
                  patch.isPending &&
                  patch.variables.id === u.id &&
                  patch.variables.body.diveAccess !== undefined
                    ? patch.variables.body.diveAccess
                    : u.diveAccess
                }
                onDive={(diveAccess) => patch.mutate({ id: u.id, body: { diveAccess } })}
                onRole={(role) => patch.mutate({ id: u.id, body: { role } })}
                onBan={() => setConfirm(u)}
              />
            ))}
          </tbody>
        </table>
      ) : null}

      {confirm ? (
        <ConfirmSheet
          title={t(confirm.banned ? "admin.users.unbanTitle" : "admin.users.banTitle", {
            name: confirm.name,
          })}
          body={t(confirm.banned ? "admin.users.unbanBody" : "admin.users.banBody", {
            name: confirm.name,
          })}
          confirmLabel={confirm.banned ? t("admin.users.unban") : t("admin.users.ban")}
          busy={patch.isPending}
          onConfirm={() => patch.mutate({ id: confirm.id, body: { banned: !confirm.banned } })}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      <CreateUser />
    </div>
  );
}

/** Offers line breaks only after "@" and "." (never mid-word); the text content stays the address. */
function breakableEmail(email: string) {
  return email.split(/(?<=[@.])/).map((part, i) => (
    <Fragment key={i}>
      {i > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ));
}

function UserRow({
  user,
  isSelf,
  busy,
  locale,
  diveAccess,
  onDive,
  onRole,
  onBan,
}: {
  user: AdminUser;
  isSelf: boolean;
  busy: boolean;
  locale: ReturnType<typeof useLocale>;
  diveAccess: boolean;
  onDive: (on: boolean) => void;
  onRole: (r: Role) => void;
  onBan: () => void;
}) {
  const { t } = useTranslation();
  const selfNoteId = isSelf ? `self-note-${user.id}` : undefined;
  return (
    <tr className="adm-usertr" data-banned={user.banned}>
      <th scope="row" className="adm-usertr__name" data-label={t("admin.users.user")}>
        <div className="adm-usertr__who">
          <span className="ds-text-headline adm-usertr__nameline">
            {user.name}
            {isSelf ? (
              <span className="ds-text-footnote text-label-secondary">
                {" "}
                · {t("admin.users.you")}
              </span>
            ) : null}
          </span>
          {user.banned ? (
            <span className="ds-badge ds-badge--closed">{t("admin.users.banned")}</span>
          ) : null}
        </div>
        <span
          className="ds-text-footnote text-label-secondary adm-usertr__email"
          title={user.email}
        >
          {breakableEmail(user.email)}
        </span>
        {isSelf ? (
          <span id={selfNoteId} className="ds-text-footnote text-label-secondary adm-usertr__note">
            {t("admin.users.selfNote")}
          </span>
        ) : null}
      </th>
      <td data-label={t("admin.users.role")}>
        <select
          className="adm-input"
          value={user.role}
          disabled={isSelf || busy}
          aria-label={t("admin.users.roleOf", { name: user.name })}
          aria-describedby={selfNoteId}
          onChange={(e) => onRole(e.target.value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`admin.role.${r}`)}
            </option>
          ))}
        </select>
      </td>
      <td data-label={t("admin.users.diveMode")}>
        <label className="adm-check">
          <input
            type="checkbox"
            role="switch"
            // admins always have dive mode: shown on, not changeable
            checked={user.role === "admin" || diveAccess}
            disabled={user.role === "admin" || busy}
            aria-label={t("admin.users.diveModeOf", { name: user.name })}
            onChange={(e) => onDive(e.target.checked)}
          />
          {user.role === "admin" ? (
            <>
              <span
                className="ds-text-footnote text-label-secondary"
                title={t("admin.users.diveModeAdmin")}
                aria-hidden="true"
              >
                {t("admin.users.diveModeShort")}
              </span>
              <span className="sr-only">{t("admin.users.diveModeAdmin")}</span>
            </>
          ) : null}
        </label>
      </td>
      <td data-label={t("admin.users.registered")} className="adm-usertr__date">
        <time dateTime={user.createdAt} title={formatTimestamp(user.createdAt, locale)}>
          {formatTimestampShort(user.createdAt, locale)}
        </time>
      </td>
      <td data-label={t("admin.users.lastSeen")} className="adm-usertr__date">
        {user.lastSeenAt ? (
          <time dateTime={user.lastSeenAt} title={formatTimestamp(user.lastSeenAt, locale)}>
            {formatTimestampShort(user.lastSeenAt, locale)}
          </time>
        ) : (
          t("admin.users.never")
        )}
      </td>
      <td
        data-label={t("admin.users.pending")}
        className="adm-usertr__num"
        data-zero={user.pendingSuggestions === 0}
      >
        {user.pendingSuggestions === 0 ? (
          <>
            <span aria-hidden="true">–</span>
            <span className="sr-only">0</span>
          </>
        ) : (
          user.pendingSuggestions
        )}
      </td>
      <td className="adm-usertr__actions">
        <Button
          variant={user.banned ? "tinted" : "destructive"}
          disabled={isSelf || busy}
          aria-describedby={selfNoteId}
          aria-label={
            user.banned
              ? t("admin.users.unbanOf", { name: user.name })
              : t("admin.users.banOf", { name: user.name })
          }
          onClick={onBan}
        >
          {user.banned ? t("admin.users.unban") : t("admin.users.ban")}
        </Button>
      </td>
    </tr>
  );
}

function CreateUser() {
  const { t } = useTranslation();
  const locale = useLocale();
  void locale;
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "moderator" as Role,
    initialPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const parsed = AdminUserCreate.safeParse(form);
      if (!parsed.success) {
        const errs: Record<string, string> = {};
        for (const i of parsed.error.issues) {
          const k = String(i.path[0] ?? "_");
          errs[k] =
            k === "initialPassword"
              ? t("admin.users.passwordShort")
              : k === "email"
                ? t("admin.users.emailInvalid")
                : t("admin.err.required");
        }
        throw Object.assign(new Error("invalid"), { errs });
      }
      return api.createUser(parsed.data);
    },
    onSuccess: async (u) => {
      setErrors({});
      setBanner(null);
      setCreated(u.email);
      setForm({ email: "", name: "", role: "moderator", initialPassword: "" });
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e) => {
      setCreated(null);
      const errs = (e as { errs?: Record<string, string> }).errs;
      if (errs) return setErrors(errs);
      if (isApiRequestError(e) && e.code === "conflict")
        return setErrors({ email: t("admin.users.emailTaken") });
      setBanner(t("admin.err.save"));
    },
  });

  return (
    <Section title={t("admin.users.create")}>
      <form
        className="grid gap-4"
        noValidate
        aria-label={t("admin.users.create")}
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          setBanner(null);
          create.mutate();
        }}
      >
        <Field label={t("admin.login.email")} error={errors.email}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input"
              type="email"
              autoComplete="off"
              value={form.email}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("admin.users.name")} error={errors.name}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input"
              autoComplete="off"
              value={form.name}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("admin.users.role")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`admin.role.${r}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label={t("admin.users.initialPassword")}
          hint={t("admin.users.passwordHint")}
          error={errors.initialPassword}
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={form.initialPassword}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setForm({ ...form, initialPassword: e.target.value })}
            />
          )}
        </Field>
        {banner ? (
          <p className="adm-error m-0" role="alert">
            {banner}
          </p>
        ) : null}
        {created ? (
          <p className="adm-ok m-0" role="status">
            {t("admin.users.created", { email: created })}
          </p>
        ) : null}
        <div>
          <Button
            type="submit"
            variant="filled"
            disabled={create.isPending}
            icon={<UserPlus aria-hidden="true" strokeWidth={1.75} />}
          >
            {t("admin.users.createSubmit")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
